import { useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import DOMPurify from 'dompurify'
import { Mail, Paperclip, X } from 'lucide-react'
import { toast } from 'sonner'
import { api, ApiError, saveCaseFile } from '@/lib/api'
import type {
  CaseDetail,
  CaseEmailEntry,
  CaseFileResult,
  CaseTimelineEntry,
} from '@/lib/api-types'
import { formatBytes, formatDate, formatDateTime, relativeTime } from '@/lib/format'
import { caseChipClasses, caseTypeLabel } from '@/lib/status'
import { cn } from '@/lib/utils'
import { validateCaseFiles, type FileValidationError } from '@/lib/files'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { DescriptionList, type DescriptionItem } from '@/components/ui/description-list'
import { PageHeader } from '@/components/ui/page-header'
import { SectionHeader } from '@/components/ui/section-header'
import { Select } from '@/components/ui/select'
import { Spinner } from '@/components/ui/spinner'
import { StatusChip } from '@/components/ui/status-chip'
import { CommentComposer } from './comment-composer'

/**
 * One request, end to end: facts, the conversation (composer above a
 * newest-first timeline), and a rail with the people and files on the case.
 * Polls every 60s so staff replies and status moves show up on their own.
 */

/** The EXACT parseable marker string the org-side flow watches for. */
const RESOLVED_COMMENT =
  '[Customer marks resolved] Resolved on our side — please close this request.'

const FILE_FAILURE_COPY: Record<string, string> = {
  file_type_not_allowed: 'this file type isn’t supported',
  file_too_large: 'over the 10 MB limit',
  upload_failed: 'the upload didn’t finish',
}

export function RequestDetailPage() {
  const { id = '' } = useParams()

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['case', id],
    queryFn: ({ signal }) => api.getCase(id, { signal }),
    refetchInterval: 60_000,
    staleTime: 15_000,
  })

  if (isPending) {
    return (
      <div className="flex justify-center py-24">
        <Spinner label="Loading this request…" />
      </div>
    )
  }

  if (error) {
    const missing = error instanceof ApiError && error.status === 404
    return (
      <div className="py-24 text-center">
        <p className="micro-label">{missing ? 'Not found' : 'Something went wrong'}</p>
        <p className="mt-3 text-[0.9375rem] text-inkMid">
          {missing
            ? 'This request doesn’t exist — or it belongs to another account.'
            : 'We couldn’t load this request.'}
        </p>
        {missing ? (
          <p className="mt-2 text-sm text-mute">
            Looking for a purchase order request?{' '}
            <Link
              to="/purchasing"
              className="rounded-sm font-semibold text-brand underline-offset-4 hover:underline"
            >
              See Purchasing
            </Link>
            .
          </p>
        ) : null}
        <div className="mt-6 flex justify-center gap-3">
          {!missing ? (
            <Button variant="neutral" size="sm" onClick={() => refetch()}>
              Try again
            </Button>
          ) : null}
          <Button asChild variant="quiet" size="sm">
            <Link to="/requests">Back to requests</Link>
          </Button>
        </div>
      </div>
    )
  }

  return <RequestDetailView detail={data} />
}

function RequestDetailView({ detail }: { detail: CaseDetail }) {
  const facts: DescriptionItem[] = [{ label: 'Type', value: caseTypeLabel(detail.type) }]
  if (detail.productName) facts.push({ label: 'Product', value: detail.productName })
  if (detail.lotNumber) facts.push({ label: 'Lot', value: detail.lotNumber, mono: true })
  if (detail.dueDate) {
    facts.push({
      label: 'Due',
      value: <time dateTime={detail.dueDate}>{formatDate(detail.dueDate)}</time>,
      mono: true,
    })
  }
  if (detail.onBehalfOfBrand) facts.push({ label: 'Brand', value: detail.onBehalfOfBrand })
  if (detail.externalReference) {
    facts.push({ label: 'Reference', value: detail.externalReference, mono: true })
  }
  if (detail.submittedByName) facts.push({ label: 'Submitted by', value: detail.submittedByName })
  facts.push({
    label: 'Opened',
    value: <time dateTime={detail.createdAt}>{formatDate(detail.createdAt)}</time>,
    mono: true,
  })
  facts.push({ label: 'Updated', value: relativeTime(detail.updatedAt), mono: true })

  return (
    <article className="mx-auto max-w-data">
      <nav aria-label="Breadcrumb" className="flex items-baseline gap-2 font-mono text-[0.8125rem]">
        <Link
          to="/requests"
          className="rounded-sm text-mute underline-offset-4 transition-colors duration-[180ms] ease-editorial hover:text-brand hover:underline"
        >
          Requests
        </Link>
        <span aria-hidden="true" className="text-muteSoft">
          ·
        </span>
        <span aria-current="page" className="text-inkMid">
          #{detail.caseNumber}
        </span>
      </nav>

      <PageHeader
        className="mt-3"
        title={detail.subject}
        meta={
          <>
            <StatusChip label={detail.statusLabel} chipClasses={caseChipClasses(detail.status)} />
            <span className="font-mono text-[0.6875rem] uppercase tracking-eyebrow">
              #{detail.caseNumber}
            </span>
          </>
        }
      />

      {detail.waitingOnYou ? (
        <div
          role="status"
          className="mt-5 rounded-md border border-amber/40 bg-amber/10 px-4 py-3 text-sm text-inkMid"
        >
          Green Line needs something from you — check the latest note and reply.
        </div>
      ) : null}

      <DescriptionList className="mt-5 border-y border-rule/60 py-4" columns={3} items={facts} />

      {detail.description ? (
        <div className="mt-5 rounded-md border border-rule/60 bg-paper/60 px-4 py-3">
          <p className="micro-label">What you asked</p>
          <p className="mt-1.5 whitespace-pre-line text-sm leading-relaxed text-inkMid">
            {detail.description}
          </p>
        </div>
      ) : null}

      <div className="mt-8 grid gap-10 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className="min-w-0">
          <section aria-labelledby="case-conversation-heading">
            <SectionHeader id="case-conversation-heading" title="Conversation" />
            <div className="mt-4">
              <CommentComposer caseId={detail.id} />
            </div>
            <Timeline caseId={detail.id} timeline={detail.timeline} />
          </section>

          {detail.statusGroup === 'open' ? (
            <ResolveRow caseId={detail.id} />
          ) : detail.resolution ? (
            <p className="mt-8 border-t border-rule/50 pt-4 text-sm text-mute">
              Closed — resolution: {detail.resolution}.
            </p>
          ) : null}
        </div>

        {/* The rail sticks on wide screens and stacks below the conversation on mobile. */}
        <aside className="space-y-8 lg:sticky lg:top-6 lg:self-start">
          <ParticipantsPanel detail={detail} />
          <FilesPanel detail={detail} />
        </aside>
      </div>
    </article>
  )
}

// ---------------------------------------------------------------------------
// Timeline (newest-first chat)
// ---------------------------------------------------------------------------

function Timeline({ caseId, timeline }: { caseId: string; timeline: CaseTimelineEntry[] }) {
  async function download(docId: string, name: string) {
    try {
      await saveCaseFile(caseId, docId, name)
    } catch {
      toast.error('That file didn’t download — try again.')
    }
  }

  return (
    <ol className="mt-6 space-y-4">
      {timeline.map((entry) => (
        <li key={entry.id}>
          {entry.kind === 'comment' ? (
            <div
              className={cn(
                'max-w-[85%] rounded-lg px-4 py-3',
                entry.side === 'you'
                  ? 'ml-auto bg-brandTint'
                  : 'mr-auto border border-rule bg-paper',
              )}
            >
              <p className="font-mono text-[0.6875rem] text-mute">
                {entry.authorName}
                {entry.side === 'greenline' ? ' · Green Line' : ''} · {formatDateTime(entry.at)}
              </p>
              <p className="mt-1.5 whitespace-pre-line text-sm leading-relaxed text-ink">
                {entry.body}
              </p>
            </div>
          ) : entry.kind === 'status' ? (
            <div className="flex justify-center">
              <span
                className={cn(
                  'inline-flex items-center whitespace-nowrap rounded-full border px-3 py-1 font-mono text-[11px]',
                  caseChipClasses(entry.toStatus),
                )}
              >
                Moved to “{entry.toLabel}” · {formatDateTime(entry.at)}
              </span>
            </div>
          ) : entry.kind === 'created' ? (
            <div className="flex justify-center">
              <span className="inline-flex items-center whitespace-nowrap rounded-full border border-rule/80 bg-paper px-3 py-1 font-mono text-[11px] text-inkMid">
                Request opened · {formatDateTime(entry.at)}
              </span>
            </div>
          ) : entry.kind === 'file' ? (
            <div className="flex justify-center">
              <span className="inline-flex max-w-full items-center gap-2 rounded-full border border-rule/80 bg-card px-3 py-1 font-mono text-[11px] text-inkMid">
                <Paperclip aria-hidden="true" className="size-3 shrink-0 text-mute" />
                <button
                  type="button"
                  onClick={() => void download(entry.docId, entry.name)}
                  className="truncate rounded-sm font-medium text-brand underline-offset-4 hover:underline"
                >
                  {entry.name}
                </button>
                <span className="whitespace-nowrap text-mute">
                  {entry.uploadedByName} · {formatDateTime(entry.at)}
                </span>
              </span>
            </div>
          ) : (
            <EmailEntryCard entry={entry} />
          )}
        </li>
      ))}
    </ol>
  )
}

/**
 * An email on the case. The server already sanitized the body; the SPA
 * sanitizes AGAIN before rendering (defense in depth), and clamps long
 * bodies behind a show-full toggle.
 */
function EmailEntryCard({ entry }: { entry: CaseEmailEntry }) {
  const [expanded, setExpanded] = useState(false)
  const cleanHtml = useMemo(() => DOMPurify.sanitize(entry.bodyHtml), [entry.bodyHtml])

  return (
    <div className="rounded-lg border border-rule bg-card">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 border-b border-rule/50 px-4 py-2.5">
        <span className="flex min-w-0 items-center gap-2">
          <Mail aria-hidden="true" className="size-3.5 shrink-0 text-mute" />
          <span className="truncate text-sm font-medium text-ink" title={entry.subject}>
            {entry.subject}
          </span>
        </span>
        <span className="font-mono text-[0.6875rem] text-mute">
          {entry.direction === 'outbound' ? 'Green Line' : entry.fromName} ·{' '}
          {formatDateTime(entry.at)}
        </span>
      </div>
      <div
        className={cn(
          'px-4 py-3 text-sm leading-relaxed text-inkMid [&_a]:text-brand [&_a]:underline',
          !expanded && 'max-h-40 overflow-hidden',
        )}
        // Sanitized twice: server-side and DOMPurify just above.
        dangerouslySetInnerHTML={{ __html: cleanHtml }}
      />
      <div className="border-t border-rule/50 px-4 py-1.5">
        <button
          type="button"
          onClick={() => setExpanded((current) => !current)}
          className="rounded-sm text-xs font-semibold text-brand underline-offset-4 hover:underline"
        >
          {expanded ? 'Collapse email' : 'Show full email'}
        </button>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Mark as resolved
// ---------------------------------------------------------------------------

function ResolveRow({ caseId }: { caseId: string }) {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: () => api.postCaseComment(caseId, RESOLVED_COMMENT),
    onSuccess: (entry) => {
      queryClient.setQueryData<CaseDetail>(['case', caseId], (current) =>
        current ? { ...current, timeline: [entry, ...current.timeline] } : current,
      )
      toast.success('Thanks — we’ve let your Green Line team know.')
    },
    onError: () => toast.error('That didn’t go through — try again.'),
  })

  return (
    <div className="mt-8 flex flex-wrap items-center justify-between gap-3 border-t border-rule/50 pt-4">
      <p className="text-sm text-mute">All sorted on your side?</p>
      <Button
        type="button"
        variant="quiet"
        size="sm"
        loading={mutation.isPending}
        onClick={() => mutation.mutate()}
      >
        Mark as resolved
      </Button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Participants rail panel
// ---------------------------------------------------------------------------

function ParticipantsPanel({ detail }: { detail: CaseDetail }) {
  const queryClient = useQueryClient()
  const [addId, setAddId] = useState('')

  const accountQuery = useQuery({
    queryKey: ['account'],
    queryFn: ({ signal }) => api.getAccount({ signal }),
    staleTime: 5 * 60_000,
  })
  const onCase = new Set(detail.participants.map((p) => p.contactId))
  const addable = (accountQuery.data?.colleagues ?? []).filter((c) => !onCase.has(c.id))

  function applyParticipants(participants: CaseDetail['participants']) {
    queryClient.setQueryData<CaseDetail>(['case', detail.id], (current) =>
      current ? { ...current, participants } : current,
    )
  }

  const addMutation = useMutation({
    mutationFn: (contactId: string) => api.addCaseParticipant(detail.id, contactId),
    onSuccess: (result) => {
      applyParticipants(result.participants)
      setAddId('')
    },
    onError: () => toast.error('That colleague didn’t get added — try again.'),
  })

  const removeMutation = useMutation({
    mutationFn: (contactId: string) => api.removeCaseParticipant(detail.id, contactId),
    onSuccess: (result) => applyParticipants(result.participants),
    onError: (error: unknown) => {
      if (error instanceof ApiError && error.code === 'cannot_remove_submitter') {
        toast.error('The person who opened the request stays on it.')
        return
      }
      toast.error('That didn’t go through — try again.')
    },
  })

  return (
    <section aria-labelledby="case-participants-heading">
      <SectionHeader id="case-participants-heading" title="Participants" />
      <Card padding="sm" className="mt-3">
        <ul className="space-y-2.5">
          {detail.participants.map((participant) => (
            <li key={participant.contactId} className="flex items-start justify-between gap-2">
              <span className="min-w-0">
                <span className="block truncate text-sm text-ink">
                  {participant.name}
                  {participant.isYou ? ' (you)' : ''}
                </span>
                {participant.isSubmitter ? (
                  <span className="micro-label">Opened this request</span>
                ) : null}
              </span>
              {!participant.isSubmitter ? (
                <button
                  type="button"
                  onClick={() => removeMutation.mutate(participant.contactId)}
                  aria-label={`Remove ${participant.name}`}
                  className="rounded-sm p-1 text-mute transition-colors duration-[180ms] ease-editorial hover:text-rust"
                >
                  <X className="size-3.5" />
                </button>
              ) : null}
            </li>
          ))}
        </ul>

        {addable.length > 0 ? (
          <div className="mt-4 border-t border-rule/40 pt-3.5">
            <label htmlFor="case-add-participant" className="micro-label block">
              Add a colleague
            </label>
            <div className="mt-1.5 flex items-center gap-2">
              <Select
                id="case-add-participant"
                value={addId}
                onChange={(event) => setAddId(event.target.value)}
                className="py-1.5 text-sm"
              >
                <option value="">Choose…</option>
                {addable.map((colleague) => (
                  <option key={colleague.id} value={colleague.id}>
                    {colleague.name}
                  </option>
                ))}
              </Select>
              <Button
                type="button"
                variant="neutral"
                size="sm"
                disabled={addId === ''}
                loading={addMutation.isPending}
                onClick={() => addMutation.mutate(addId)}
              >
                Add
              </Button>
            </div>
          </div>
        ) : null}
      </Card>
    </section>
  )
}

// ---------------------------------------------------------------------------
// Files rail panel
// ---------------------------------------------------------------------------

function FilesPanel({ detail }: { detail: CaseDetail }) {
  const queryClient = useQueryClient()
  const inputRef = useRef<HTMLInputElement>(null)
  const [rejections, setRejections] = useState<FileValidationError[]>([])
  const [results, setResults] = useState<CaseFileResult[]>([])

  const uploadMutation = useMutation({
    mutationFn: (files: File[]) => api.uploadCaseFiles(detail.id, files),
    onSuccess: (result) => {
      setResults(result.files)
      // New files land on the case AND its timeline — refetch the whole row.
      void queryClient.invalidateQueries({ queryKey: ['case', detail.id] })
    },
    onError: () => toast.error('The upload didn’t go through — try again.'),
  })

  function pick(picked: File[]) {
    const { accepted, rejected } = validateCaseFiles(picked)
    setRejections(rejected)
    setResults([])
    if (accepted.length > 0) uploadMutation.mutate(accepted)
  }

  async function download(docId: string, name: string) {
    try {
      await saveCaseFile(detail.id, docId, name)
    } catch {
      toast.error('That file didn’t download — try again.')
    }
  }

  return (
    <section aria-labelledby="case-files-heading">
      <SectionHeader id="case-files-heading" title="Files" />
      <Card padding="sm" className="mt-3">
        {detail.files.length === 0 ? (
          <p className="text-sm text-mute">Nothing attached yet.</p>
        ) : (
          <ul className="space-y-2.5">
            {detail.files.map((file) => (
              <li key={file.docId} className="min-w-0">
                <button
                  type="button"
                  onClick={() => void download(file.docId, file.name)}
                  title={file.name}
                  className="block max-w-full truncate rounded-sm text-left text-sm font-medium text-brand underline-offset-4 hover:underline"
                >
                  {file.name}
                </button>
                <p className="font-mono text-[0.6875rem] text-mute">
                  {formatBytes(file.sizeBytes)} · {file.uploadedByName} ·{' '}
                  {relativeTime(file.uploadedAt)}
                </p>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-4 border-t border-rule/40 pt-3.5">
          <input
            ref={inputRef}
            type="file"
            multiple
            aria-label="Add files to this request"
            className="sr-only"
            onChange={(event) => {
              pick(Array.from(event.target.files ?? []))
              event.target.value = ''
            }}
          />
          <Button
            type="button"
            variant="neutral"
            size="sm"
            loading={uploadMutation.isPending}
            onClick={() => inputRef.current?.click()}
          >
            Add files
          </Button>

          {rejections.length > 0 ? (
            <ul role="alert" className="mt-2.5 space-y-1 text-xs font-medium text-rust">
              {rejections.map((rejection) => (
                <li key={rejection.name}>
                  {rejection.name}: {rejection.reason}.
                </li>
              ))}
            </ul>
          ) : null}

          {results.length > 0 ? (
            <ul className="mt-2.5 space-y-1 text-xs">
              {results.map((result) => (
                <li
                  key={result.name}
                  className={result.ok ? 'text-inkMid' : 'font-medium text-rust'}
                >
                  <span className="font-mono">{result.name}</span>
                  {result.ok
                    ? ' — attached.'
                    : ` — ${FILE_FAILURE_COPY[result.error ?? ''] ?? 'it didn’t attach'}.`}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </Card>
    </section>
  )
}
